export function delayPromise<R>(duration: number): Promise<R> {
  return new Promise<R>((resolve) => {
    window.setTimeout(resolve, duration);
  });
}

export const callWithRetryRejectedResponseMessage =
  'Promised was resolved but result was treated as bad';

interface PromiseRetryOptions<Result> {
  isBadResultFn?(result: Result): boolean;
  maxTries: number; // max retry count for both logical errors (based on isBadResultFn) and unknown failures
  maxTriesForUnknownFailures?: number; // like network failures ot BE native errors of some unexpected bugs
  retryInterval: number;
}

export function callWithRetry<Result>(
  callFn: () => Promise<Result>,
  {
    retryInterval,
    maxTries,
    isBadResultFn,
    maxTriesForUnknownFailures,
  }: PromiseRetryOptions<Result>,
): Promise<Result> {
  const retryWhenBadResult = (): Promise<Result> => {
    if (maxTries > 1) {
      return delayPromise(retryInterval).then(() =>
        callWithRetry(callFn, {
          retryInterval,
          maxTries: maxTries - 1,
          isBadResultFn,
        }),
      );
    }

    return Promise.reject({ response: callWithRetryRejectedResponseMessage });
  };

  const retryWhenUnknownFailure = (error): Promise<Result> => {
    const triesLeft =
      maxTriesForUnknownFailures === undefined
        ? maxTries
        : maxTriesForUnknownFailures;

    if (triesLeft > 1) {
      return delayPromise(retryInterval).then(() =>
        callWithRetry(callFn, {
          retryInterval,
          maxTries: maxTries - 1,
          maxTriesForUnknownFailures:
            maxTriesForUnknownFailures === undefined
              ? undefined
              : maxTriesForUnknownFailures - 1,
          isBadResultFn,
        }),
      );
    }

    return Promise.reject(error);
  };

  return callFn().then((result: Result) => {
    if (isBadResultFn && isBadResultFn(result)) {
      return retryWhenBadResult();
    }

    return result;
  }, retryWhenUnknownFailure);
}
